import { StateWithSubjectService } from "@hub/services/state-with-subject.service";
import { Observable, Subscription } from "rxjs";
import {
  FollowJwtRabbitStatus,
  MatrixQuestName,
  MatrixQuestStatus,
  MatrixServiceState,
  MatrixState,
} from "@hub/models/challenges/matrix";
import axios from "axios";
import {
  PuzzleCard,
  PuzzlePiecesBundle,
  PuzzlePieceType,
} from "@hub/models/challenges/puzzle";
import isString from "lodash/isString";
import {
  ApiPuzzlePiecesUpdateData,
  ApiPuzzlePiecesUpdateResponse,
} from "@hub/models/challenges/api/puzzle/pieces/update";
import {
  JWT_GAME_DB_NAME,
  jwtGameDb,
  RabbitModel,
} from "@hub/services/store/rabbit-store";
import { JwtGameEngineSupport } from "@hub/models/challenges/engine-support";
import Dexie, { liveQuery, Subscription as DexieSubscription } from "dexie";
import { toast } from "react-toastify";
import Router from "next/router";
import { JwtGameAvalability } from "@hub/models/challenges/jwt-game-avalability";

const initialState: MatrixServiceState = {
  state: MatrixState.IDLE,
  siteRabbits: [],
  eventRabbits: [],
  quests: {
    chasingRabbits: {
      name: MatrixQuestName.CHASING_RABBITS,
      status: MatrixQuestStatus.INCOMPLETE,
    },
    downTheRabbitHole: {
      name: MatrixQuestName.DOWN_THE_RABBIT_HOLE,
      status: MatrixQuestStatus.INCOMPLETE,
    },
    yourEvidence: {
      name: MatrixQuestName.YOUR_EVIDENCE,
      status: MatrixQuestStatus.INCOMPLETE,
    },
  },
  activeQuestTab: {
    name: MatrixQuestName.CHASING_RABBITS,
    status: MatrixQuestStatus.INCOMPLETE,
  },
  gameStatus: FollowJwtRabbitStatus.UNKNOWN,
  engineSupport: JwtGameEngineSupport.UNKNOWN,
  jwt: "",
};

class _MatrixService extends StateWithSubjectService<MatrixServiceState> {
  GAME_ID = "follow-the-jwt-rabbit";
  SITE_PIECES_COUNT: number = 10;
  EVENT_PIECES_COUNT: number = 3;
  TOTAL_PIECES_COUNT = this.SITE_PIECES_COUNT + this.EVENT_PIECES_COUNT;

  $engineSupport: Observable<JwtGameEngineSupport> = this.select(
    (state) => state.engineSupport
  );
  $gameStatus: Observable<FollowJwtRabbitStatus> = this.select(
    (state) => state.gameStatus
  );
  $siteRabbits: Observable<RabbitModel[]> = this.select(
    (state) => state.siteRabbits
  );
  $eventRabbits: Observable<RabbitModel[]> = this.select(
    (state) => state.eventRabbits
  );
  $jwt: Observable<string> = this.select((state) => state.jwt);

  gameStatusSubscription: Subscription | null = null;
  gameStatusDexieSubscription: DexieSubscription | null = null;
  rabbitsDexieSubscription: DexieSubscription | null = null;
  siteRabbitsDexieSubscription: DexieSubscription | null = null;
  eventRabbitsDexieSubscription: DexieSubscription | null = null;

  constructor() {
    super(initialState);

    const puzzleJwtGameAvailability =
      process.env.NEXT_PUBLIC_PUZZLE_JWT_GAME_AVAILABILITY;

    if (puzzleJwtGameAvailability === JwtGameAvalability.OFFLINE) {
      return;
    }

    this.preInit();
  }

  preInit = async () => {
    this.gameStatusSubscription = this.$gameStatus.subscribe((data) => {
      switch (data) {
        case FollowJwtRabbitStatus.NOT_BUILT: {
          this.buildPuzzle();

          break;
        }
        default: {
        }
      }
    });
  };

  checkSupport = async () => {
    try {
      const db = new Dexie("appDB");
      await db.version(1).stores({ engineCheck: "" });
      await db.open();

      this.patchState({ engineSupport: JwtGameEngineSupport.SUPPORTED });
    } catch (e) {
      this.patchState({ engineSupport: JwtGameEngineSupport.NOT_SUPPORTED });
    }
  };

  blockGame = () => {
    this.patchState({
      gameStatus: FollowJwtRabbitStatus.BLOCKED,
    });
  };

  init = async () => {
    const $gameQueryResult = liveQuery(() => {
      return jwtGameDb.game.where("id").equals(this.GAME_ID).first();
    });

    const $rabbitsQueryResult = liveQuery(async () => {
      return jwtGameDb.rabbits.toArray();
    });

    const $siteRabbitsQueryResult = liveQuery(async () => {
      return jwtGameDb.rabbits
        .filter((rabbit) => rabbit.type === PuzzlePieceType.DEV_CENTER)
        .toArray();
    });

    const $eventRabbitsQueryResult = liveQuery(async () => {
      return jwtGameDb.rabbits
        .filter((rabbit) => rabbit.type === PuzzlePieceType.SESSIONS)
        .toArray();
    });

    this.gameStatusDexieSubscription = $gameQueryResult.subscribe((data) => {
      if (!data) {
        this.patchState({ gameStatus: FollowJwtRabbitStatus.NOT_BUILT });

        return;
      }

      if (data && data.status) {
        this.patchState({ gameStatus: data.status });
      }

      if (data && data.jwt) {
        this.patchState({ jwt: data.jwt });
      }
    });

    this.rabbitsDexieSubscription = $rabbitsQueryResult.subscribe(
      async (data) => {
        if (data) {
          const game = await jwtGameDb.game.get(this.GAME_ID);

          if (!game) {
            return;
          }

          if (
            data.length === this.TOTAL_PIECES_COUNT &&
            !game.findJwtNotification
          ) {
            toast.success(
              "You got all rabbits! Time to crack down the JWT...!",
              {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
              }
            );

            jwtGameDb.game.update(this.GAME_ID, {
              findJwtNotification: true,
            });
          }
        }
      }
    );

    this.siteRabbitsDexieSubscription = $siteRabbitsQueryResult.subscribe(
      async (data) => {
        if (data) {
          this.patchState({ siteRabbits: data });

          const game = await jwtGameDb.game.get(this.GAME_ID);

          if (!game) {
            return;
          }

          if (
            data.length === this.SITE_PIECES_COUNT &&
            !game.siteRabbitsNotification
          ) {
            toast.success("You got all the DevCenter rabbits!", {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light",
            });

            jwtGameDb.game.update(this.GAME_ID, {
              siteRabbitsNotification: true,
            });
          }
        }
      }
    );

    this.eventRabbitsDexieSubscription = $eventRabbitsQueryResult.subscribe(
      async (data) => {
        if (data) {
          this.patchState({ eventRabbits: data });

          const game = await jwtGameDb.game.get(this.GAME_ID);

          if (!game) {
            return;
          }

          if (
            data.length === this.EVENT_PIECES_COUNT &&
            !game.eventRabbitsFoundNotification
          ) {
            toast.success("You got all the Event rabbits!", {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light",
            });

            jwtGameDb.game.update(this.GAME_ID, {
              eventRabbitsFoundNotification: true,
            });
          }
        }
      }
    );
  };

  cleanUp = () => {
    if (this.gameStatusSubscription) {
      this.gameStatusSubscription.unsubscribe();
    }

    if (this.gameStatusDexieSubscription) {
      this.gameStatusDexieSubscription.unsubscribe();
    }

    if (this.siteRabbitsDexieSubscription) {
      this.siteRabbitsDexieSubscription.unsubscribe();
    }

    if (this.eventRabbitsDexieSubscription) {
      this.eventRabbitsDexieSubscription.unsubscribe();
    }
  };

  buildPuzzle = async (): Promise<{
    result: string | null;
    error: string | null;
  }> => {
    try {
      const existingGame = await jwtGameDb.game
        .where("id")
        .equals(this.GAME_ID)
        .first();

      if (existingGame) {
        return { result: "Game already exists", error: null };
      }

      await jwtGameDb.game.add({
        id: this.GAME_ID,
        status: FollowJwtRabbitStatus.NOT_STARTED,
        siteRabbitsNotification: false,
        eventRabbitsFoundNotification: false,
        findJwtNotification: false,
        jwt: "",
      });

      this.patchState({ gameStatus: FollowJwtRabbitStatus.NOT_STARTED });

      return {
        result: "Game built!",
        error: null,
      };
    } catch (e) {
      return {
        result: null,
        error: "Unable to build game. Please try again later.",
      };
    }
  };

  startGame = async (): Promise<{
    result: string | null;
    error: string | null;
  }> => {
    try {
      const existingGame = await jwtGameDb.game
        .where("id")
        .equals(this.GAME_ID)
        .first();

      if (
        existingGame &&
        existingGame.status === FollowJwtRabbitStatus.NOT_STARTED
      ) {
        await jwtGameDb.game.update(this.GAME_ID, {
          status: FollowJwtRabbitStatus.WIP,
        });

        return { result: "Game started!", error: null };
      }

      if (existingGame) {
        return { result: "Nothing to start here...", error: null };
      }

      await jwtGameDb.game.add({
        id: this.GAME_ID,
        status: FollowJwtRabbitStatus.WIP,
        siteRabbitsNotification: false,
        eventRabbitsFoundNotification: false,
        findJwtNotification: false,
        jwt: "",
      });

      this.patchState({ gameStatus: FollowJwtRabbitStatus.WIP });

      return {
        result: "Game started!",
        error: null,
      };
    } catch (e) {
      return {
        result: null,
        error: "Unable to start game. Please try again later.",
      };
    }
  };

  quitGame = async (): Promise<{
    result: string | null;
    error: string | null;
  }> => {
    try {
      const existingGame = await jwtGameDb.game
        .where("id")
        .equals(this.GAME_ID)
        .first();

      if (!existingGame) {
        return {
          result: null,
          error: "No game exists!",
        };
      }

      await jwtGameDb.game.clear();
      await jwtGameDb.rabbits.clear();
      await Router.push("/challenges");

      this.patchState({
        gameStatus: FollowJwtRabbitStatus.NOT_STARTED,
        siteRabbits: [],
        eventRabbits: [],
        jwt: "",
      });

      return {
        result: "Thanks for trying out the game!",
        error: null,
      };
    } catch (e) {
      return {
        result: null,
        error: "Unable to quit game. Please try again later!",
      };
    }
  };

  offlineCleanUp = async () => {
    if (await Dexie.exists(JWT_GAME_DB_NAME)) {
      await Dexie.delete(JWT_GAME_DB_NAME);

      this.patchState({
        gameStatus: FollowJwtRabbitStatus.NOT_STARTED,
        siteRabbits: [],
        eventRabbits: [],
        jwt: "",
      });
    }

    if (await Dexie.exists("appDB")) {
      await Dexie.delete("appDB");
    }
  };

  completeGame = (jwt: string) => {
    jwtGameDb.game.update(this.GAME_ID, {
      status: FollowJwtRabbitStatus.COMPLETE,
      jwt: jwt,
    });

    this.patchState({
      gameStatus: FollowJwtRabbitStatus.COMPLETE,
      jwt: jwt,
    });
  };

  isAllowedGameStatus = (status: string) =>
    status === FollowJwtRabbitStatus.NOT_STARTED ||
    FollowJwtRabbitStatus.WIP ||
    FollowJwtRabbitStatus.COMPLETE;

  areValidJwtPieces = (pieces: any): string[] | null => {
    if (!Array.isArray(pieces)) {
      return null;
    }

    if (pieces.length > this.TOTAL_PIECES_COUNT) {
      return null;
    }

    for (let i = 0; i < pieces.length; i++) {
      const rabbit = pieces[i];

      if (!rabbit) {
        return null;
      }

      if (!isString(rabbit)) {
        return null;
      }
    }

    return pieces as string[];
  };

  getPieces = async (): Promise<PuzzlePiecesBundle | null> => {
    const rabbits = await jwtGameDb.rabbits.toArray();
    const game = await jwtGameDb.game.get(this.GAME_ID);

    if (!(rabbits && game && game.status)) {
      return null;
    }

    if (!this.isAllowedGameStatus(game.status)) {
      return null;
    }

    const sitePieces = rabbits
      .filter((rabbit) => rabbit.type === PuzzlePieceType.DEV_CENTER)
      .map((rabbit) => rabbit.piece);
    const eventPieces = rabbits
      .filter((rabbit) => rabbit.type === PuzzlePieceType.SESSIONS)
      .map((rabbit) => rabbit.piece);

    if (!this.areValidJwtPieces([...sitePieces, ...eventPieces])) {
      return null;
    }

    return {
      sitePieces: sitePieces,
      eventPieces: eventPieces,
    };
  };

  logPiece = async (card: PuzzleCard, image: number) => {
    try {
      const { data } = await axios.post<ApiPuzzlePiecesUpdateResponse>(
        "/resources/api/puzzle/pieces/update",
        {
          id: card.id,
        } as ApiPuzzlePiecesUpdateData
      );

      if (!data.success) {
        return null;
      }

      const { piece } = data.success;

      const existingPiece = await jwtGameDb.rabbits
        .where("piece")
        .equals(piece)
        .first();

      if (existingPiece) {
        return null;
      }

      await jwtGameDb.rabbits.add({
        order: image,
        piece: card.value,
        hints: card.hints,
        type: card.type,
      });
    } catch (e) {
      console.error("Unable to save piece.");
    }
  };
}

export const MatrixService = new _MatrixService();
